import {
  getAvailableOnRampCurrencies,
  getOnRampAccountByCurrency,
  getOnRampAccountFields,
  getOnRampRailsForCurrency,
  getPreferredRailType,
  getRailShortLabel,
} from './onRampHelpers';

const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF',
  PLN: 'zł',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  BDT: '৳',
  BRL: 'R$',
  MXN: 'MX$',
};

const CURRENCY_SORT_ORDER = ['EUR', 'USD', 'GBP', 'CHF'];

export function getCurrencySymbol(currencyCode) {
  const code = String(currencyCode || '').toUpperCase();
  return CURRENCY_SYMBOLS[code] || code;
}

export function formatAccountAmount(amount, currencyCode) {
  const value = Number(amount) || 0;
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  const symbol = getCurrencySymbol(currencyCode);
  if (symbol === currencyCode) {
    return `${formatted} ${currencyCode}`;
  }
  return `${formatted} ${symbol}`;
}

export function formatAccountIdentifier(bankAccount, { railsCount = 1 } = {}) {
  if (!bankAccount && railsCount <= 1) return null;

  if (railsCount > 1) {
    return `${railsCount} pay-in methods`;
  }

  if (!bankAccount) return null;

  const iban = String(bankAccount.iban || '').replace(/\s/g, '');
  if (iban) {
    const spaced = iban.replace(/(.{4})/g, '$1 ').trim();
    if (spaced.length <= 22) return spaced;
    return `${spaced.slice(0, 22).trim()}…`;
  }

  const accountNumber = String(bankAccount.accountNumber || '').replace(/\s/g, '');
  if (accountNumber) {
    const last4 = accountNumber.slice(-4);
    return `···· ${last4}`;
  }

  const routing = String(bankAccount.routingNumber || '').trim();
  if (routing) {
    return `${getRailShortLabel(bankAccount.type)} · ${routing}`;
  }

  return getRailShortLabel(bankAccount.type) || null;
}

export function getRailDetailFields(rails, railType) {
  if (!Array.isArray(rails) || rails.length === 0) return [];
  const preferredType = railType || getPreferredRailType(rails);
  const match =
    rails.find((rail) => String(rail.type).toLowerCase() === String(preferredType || '').toLowerCase()) || rails[0];
  return getOnRampAccountFields(match?.bankAccount);
}

export function formatSpacedIdentifier(value) {
  const compact = String(value || '').replace(/\s/g, '');
  if (!compact) return '';
  return compact.replace(/(.{4})/g, '$1 ').trim();
}

export function getAccountTransferSubtitle(rails) {
  if (!Array.isArray(rails) || rails.length === 0) return 'Bank transfer';
  if (rails.length === 1) {
    const type = String(rails[0].type || '').toLowerCase();
    if (type === 'sepa') return 'SEPA transfer';
    return `${rails[0].shortLabel || getRailShortLabel(rails[0].type)} transfer`;
  }
  return rails.map((rail) => rail.shortLabel || getRailShortLabel(rail.type)).filter(Boolean).join(' · ');
}

export function getPrimaryDepositField(bankAccount) {
  if (!bankAccount || typeof bankAccount !== 'object') return null;

  const iban = String(bankAccount.iban || '').trim();
  if (iban) {
    return { label: 'IBAN', value: formatSpacedIdentifier(iban), copyValue: iban.replace(/\s/g, '') };
  }

  const accountNumber = String(bankAccount.accountNumber || '').trim();
  if (accountNumber) {
    return {
      label: 'Account number',
      value: formatSpacedIdentifier(accountNumber),
      copyValue: accountNumber.replace(/\s/g, ''),
    };
  }

  return null;
}

export function getTransferMethodDetail(bankAccount) {
  if (!bankAccount || typeof bankAccount !== 'object') return null;

  const type = String(bankAccount.type || '').toLowerCase();
  const routing = String(bankAccount.routingNumber || '').trim();
  const swift = String(bankAccount.swiftCode || bankAccount.bic || '').trim();
  const sortCode = String(bankAccount.sortCode || '').trim();

  if ((type === 'ach' || type === 'fedwire' || type === 'fednow' || type === 'wire') && routing) {
    let hint = 'Domestic US wire · same-day possible · bank fees may apply';
    if (type === 'ach') {
      hint = 'Domestic US transfers · 1–2 business days · no fee';
    } else if (type === 'fednow') {
      hint = 'Instant domestic US transfer · bank availability varies';
    }

    return {
      label: type === 'ach' ? 'ACH routing number' : 'Routing number',
      value: formatSpacedIdentifier(routing),
      copyValue: routing.replace(/\s/g, ''),
      hint,
    };
  }

  if (type === 'swift' && swift) {
    return {
      label: 'SWIFT / BIC',
      value: swift,
      copyValue: swift.replace(/\s/g, ''),
      hint: 'International transfer · 1–5 business days · bank fees may apply',
    };
  }

  if (type === 'sortcode' && sortCode) {
    return {
      label: 'Sort code',
      value: sortCode,
      copyValue: sortCode.replace(/\s/g, ''),
      hint: 'UK Faster Payments · usually same day',
    };
  }

  return null;
}

export function getDepositInstructionsCopy(bankAccount, currency = 'EUR', { railsCount = 1 } = {}) {
  if (railsCount > 1) {
    return 'Choose the transfer method your bank supports, then use the details below.';
  }

  const type = String(bankAccount?.type || '').toLowerCase();
  const curr = String(currency || bankAccount?.currency || 'EUR').toUpperCase();

  if (type === 'sepa' || curr === 'EUR') {
    return 'Transfer from your bank via SEPA using the details below. Funds usually arrive the same business day.';
  }
  if (type === 'ach') {
    return 'Transfer from your US bank via ACH using the details below. Funds usually arrive in 1–2 business days.';
  }
  if (type === 'fedwire' || type === 'wire') {
    return 'Transfer via Fedwire / wire using the details below. Same-day arrival is possible; bank fees may apply.';
  }
  if (type === 'fednow') {
    return 'Transfer via FedNow using the details below. Arrival is typically instant when supported by your bank.';
  }
  if (type === 'swift') {
    return 'Transfer via SWIFT using the details below. International transfers usually take 1–5 business days.';
  }
  if (type === 'sortcode' || curr === 'GBP') {
    return 'Transfer via Faster Payments using the details below. Funds usually arrive the same day.';
  }
  return 'Transfer from your bank using the details below. Arrival times depend on your bank and payment method.';
}

export function buildWalletAccounts({ balances, onRampAccounts, supportedCurrencies }) {
  const balanceMap = balances && typeof balances === 'object' ? balances : {};
  const currencySet = new Set([
    ...Object.keys(balanceMap).map((code) => String(code).toUpperCase()),
    ...getAvailableOnRampCurrencies(onRampAccounts, supportedCurrencies),
  ]);

  const currencies = Array.from(currencySet).filter(Boolean).sort((a, b) => {
    const ai = CURRENCY_SORT_ORDER.indexOf(a);
    const bi = CURRENCY_SORT_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return currencies.map((currency) => {
    const rawBalance = balanceMap[currency] ?? balanceMap[currency.toLowerCase()];
    const amount = Number(rawBalance) || 0;
    const rails = getOnRampRailsForCurrency(onRampAccounts, currency);
    const preferredRailType = getPreferredRailType(rails);
    const depositAccount = getOnRampAccountByCurrency(onRampAccounts, currency, preferredRailType);

    return {
      id: currency,
      currency,
      title: `${currency} Account`,
      amount,
      amountLabel: formatAccountAmount(amount, currency),
      identifier: formatAccountIdentifier(depositAccount, { railsCount: rails.length }),
      preferredRailType,
      rails,
      depositAccount,
      detailFields: getOnRampAccountFields(depositAccount),
    };
  });
}
